<template>
  <v-dialog
    persistent
    v-model="openDialog"
    @keydown.esc="openDialog = false"
    max-width="600"
    :fullscreen="$vuetify.breakpoint.smAndDown"
  >
    <v-card>
      <v-card-title class="title pb-0"> Export Conversation </v-card-title>
      <v-card-text>
        <v-flex class="px-2">
          <div class="text-xs-left">Export format</div>
          <v-select :items="exportFormats" class="pt-0 mt-0" v-model="format" />
        </v-flex>

        <v-flex class="px-2">
          <div class="text-xs-left">Timeframe</div>
          <v-select
            :items="exportOptions"
            class="pt-0 mt-0"
            v-model="timeframe"
          />
        </v-flex>

        <v-layout row v-if="timeframe === 'Custom'">
          <v-flex xs12 sm6>
            <v-menu
              v-model="menu"
              :close-on-content-click="false"
              :nudge-right="40"
              lazy
              transition="scale-transition"
              offset-y
              full-width
              min-width="290px"
            >
              <template v-slot:activator="{ on }">
                <v-flex class="px-2">
                  <div class="text-xs-left">Date from</div>
                  <v-text-field
                    v-model="date"
                    append-icon="calendar_today"
                    class="pt-0 mt-0"
                    readonly
                    v-on="on"
                  ></v-text-field>
                </v-flex>
              </template>
              <v-date-picker
                v-model="date"
                @input="menu = false"
              ></v-date-picker>
            </v-menu>
          </v-flex>
          <v-flex xs12 sm6 class="ml-2">
            <v-menu
              v-model="menuTo"
              :close-on-content-click="false"
              :nudge-right="40"
              lazy
              transition="scale-transition"
              offset-y
              full-width
              min-width="290px"
            >
              <template v-slot:activator="{ on }">
                <v-flex class="px-2">
                  <div class="text-xs-left">Date to</div>
                  <v-text-field
                    v-model="dateTo"
                    append-icon="calendar_today"
                    class="pt-0 mt-0"
                    readonly
                    v-on="on"
                  ></v-text-field>
                </v-flex>
              </template>
              <v-date-picker
                v-model="dateTo"
                :min="date"
                @input="menuTo = false"
              ></v-date-picker>
            </v-menu>
          </v-flex>
        </v-layout>
      </v-card-text>
      <v-card-actions>
        <v-spacer />
        <v-btn
          class="primary text-none"
          :loading="exporting"
          @click="exportConversation()"
        >
          Export
        </v-btn>
        <v-btn class="error--text text-none" flat @click="openDialog = false">
          Cancel
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import API from '@/api';
import ConversationBox from '@/components/ConversationBox';
import moment from 'moment';

export default {
  name: 'ExportConversationDialog',
  props: {
    value: {
      type: Boolean,
      default: false,
    },
    conversationId: {
      type: Number,
      default: null,
    },
    selectedUsers: {
      type: String,
      default: undefined,
    },
  },
  components: {
    ConversationBox,
  },
  watch: {
    value(newVal) {
      this.openDialog = newVal;
    },
    openDialog(newVal) {
      this.$emit('input', newVal);
    },
  },
  methods: {
    async exportConversation() {
      this.exporting = true;
      let dateFrom;
      let dateTo;
      switch (this.timeframe) {
        case 'Custom':
          dateFrom = moment(this.date, moment.ISO_8601);
          dateTo = moment(this.dateTo, moment.ISO_8601);
          break;
        case 'Last week':
          const lastWeek = moment().subtract(1, 'week');
          dateFrom = lastWeek.clone().startOf('week');
          dateTo = lastWeek.clone().endOf('week');
          break;
        case 'Last month':
          const lastMonth = moment().subtract(1, 'month');
          dateFrom = lastMonth.clone().startOf('month');
          dateTo = lastMonth.clone().endOf('month');
          break;
        case 'Last quarter':
          const lastQuarter = moment().subtract(1, 'quarter');
          dateFrom = lastQuarter.clone().startOf('quarter');
          dateTo = lastQuarter.clone().endOf('quarter');
          break;
        case 'Last year':
          const lastYear = moment().subtract(1, 'year');
          dateFrom = lastYear.clone().startOf('year');
          dateTo = lastYear.clone().endOf('year');
          break;
      }

      if (dateFrom && dateTo) {
        dateFrom = dateFrom.format('DD-MM-yyyy');
        dateTo = dateTo.format('DD-MM-yyyy');
      }

      if (this.format === 'CSV') {
        const response = await API.conversation.exportConversation(
          this.conversationId,
          dateFrom,
          dateTo,
        );

        if (response.data.error) {
          console.log(response.data.error);
          this.$store.dispatch('application/setErrorNotification', {
            message: response.data.error,
          });
          this.exporting = false;
        } else {
          this.exporting = false;
          this.saveAs(
            response.data,
            `Conversation Export for ${this.selectedUsers} (${moment().format(
              'lll',
            )}).xls`,
          );
        }
      } else {
        const response = await API.conversation.exportConversationAsPDF(
          this.conversationId,
          dateFrom,
          dateTo,
        );

        this.exporting = false;
      }

      this.openDialog = false;
    },
    saveAs(uri, filename) {
      let link = document.createElement('a');
      if (typeof link.download === 'string') {
        link.href = uri;
        link.download = filename;
        // Firefox requires the link to be in the body
        document.body.appendChild(link);
        // simulate click
        link.click();
        // remove the link when done
        document.body.removeChild(link);
      } else {
        window.open(uri, '_blank');
      }
    },
  },
  data() {
    return {
      timeframe: 'All time',
      format: 'CSV',
      openDialog: false,
      exporting: false,
      exportOptions: [
        'All time',
        'Last week',
        'Last month',
        'Last quarter',
        'Last year',
        'Custom',
      ],
      exportFormats: ['CSV', 'PDF'],
      date: new Date().toISOString().substr(0, 10),
      menu: false,
      dateTo: new Date().toISOString().substr(0, 10),
      menuTo: false,
    };
  },
  created() {
    this.openDialog = this.value;
  },
};
</script>

<style scoped></style>
